import { eventBus, GlobalEvents } from "../global-events";
import { useWebpBackgroundImage } from "../webp-background-images";
import { useWecomaDatalayer } from "./use-wecoma-datalayer";
import { useWecomaIntegration } from "./use-wecoma-integration";
import { WecomaConstants } from "./wecoma-constants";

type Themes = "map";
const overlayClass = "gdpr-overlay";
const buttonClass = "gdpr-overlay__button";
const prefCenterLinkClass = "consent-overlay-preference-center";

const preferenceCenterLinkUrl = `${appSettings["Integrations:Wecoma:Domain"]}?key=${WecomaConstants.WECOMA_CODE_DEH}&locale=cs-CZ&authKeys=${WecomaConstants.WECOMA_AUTHKEY}&source=thirdpartycontent`;

export async function handleConsentOverlay(
    elementOrSelector: string | HTMLElement,
    externalVideoUrl?: string,
    theme?: Themes,
    overlayMinHeight = 280
): Promise<boolean> {
    const element =
        elementOrSelector instanceof HTMLElement
            ? elementOrSelector
            : (document.querySelector(elementOrSelector) as HTMLElement);

    if (!element) return false;

    // remove previously added overlay
    (element as HTMLElement).style.minHeight = null;
    element.querySelector("." + overlayClass)?.remove();

    const { thirdPartyConsented } = useWecomaIntegration();

    const hasRequiredConsent = await thirdPartyConsented();

    if (hasRequiredConsent) return false;

    const { pushThirdParty } = useWecomaDatalayer();
    pushThirdParty();

    // add new overlay
    (element as HTMLElement).style.minHeight = `${overlayMinHeight}px`;
    element.insertAdjacentHTML("beforeend", getOverlayHtml(externalVideoUrl, theme));
    useWebpBackgroundImage(element.querySelector("." + overlayClass));

    document.querySelectorAll(`.${buttonClass}`).forEach((el) => {
        el.removeEventListener("click", buttonClickHandler);
        el.addEventListener("click", buttonClickHandler);
    });

    document.querySelectorAll(`.${prefCenterLinkClass}`).forEach((el) => {
        if (el instanceof HTMLAnchorElement) {
            el.href = preferenceCenterLinkUrl;
        }
        el.removeEventListener("click", prefCenterLinkClickHandler);
        el.addEventListener("click", prefCenterLinkClickHandler);
    });

    return true;
}

async function buttonClickHandler() {
    document.querySelectorAll("." + overlayClass).forEach((el) => {
        el.remove();
    });

    const { consentToThirdParty } = useWecomaIntegration();
    await consentToThirdParty();

    eventBus.emit(GlobalEvents.ConsentsChanged);
}

function prefCenterLinkClickHandler(event) {
    const variant = (event.currentTarget as HTMLElement)?.classList?.contains("gdpr-overlay__link") ? "button" : "text";

    const { pushOverlayRedirectToPreferenceCenter } = useWecomaDatalayer();
    pushOverlayRedirectToPreferenceCenter(variant);
}

function getOverlayHtml(externalVideoUrl?: string, theme?: Themes) {
    let header = environment.gdprOverlay.header;
    let message = environment.gdprOverlay.message;
    let button = environment.gdprOverlay.button;
    let link = environment.gdprOverlay.link;
    let linkUrl = environment.gdprOverlay.linkUrl;
    let bg = environment.gdprOverlay.bg;

    if (theme === "map") {
        const overlayConfig = (window as any).mapOverlay;
        header = overlayConfig.header;
        message = overlayConfig.message;
        button = overlayConfig.button;
        link = overlayConfig.link;
        linkUrl = overlayConfig.linkUrl;
        bg = overlayConfig.bg;
    }

    return `
        <div class="${overlayClass}" data-src="${bg}">
            <div class="gdpr-overlay__content">
                <div>
                    <div class="gdpr-overlay__title">${header}</div>
                    <div class="gdpr-overlay__text">
                        ${message}
                        ${
                            externalVideoUrl
                                ? ` Video můžete zhlédnout přímo na webové stránce poskytovatele <a href="${externalVideoUrl}" target="_blank" rel="noopener noreferrer"><strong>zde</strong></a>.`
                                : ""
                        }
                    </div>
                </div>
                <div class="gdpr-overlay__actions">
                    <button type="button" class="${buttonClass} btn btn-secondary btn-sm">${button}</button>
                    <a href="${linkUrl ? linkUrl : preferenceCenterLinkUrl}" target="_blank" class="gdpr-overlay__link${
        linkUrl ? "" : " " + prefCenterLinkClass
    }">${link}</a>
                </div>
            </div>
        </div>
    `;
}
